import { useState } from 'react';
import type { Address } from 'viem';
import { ContractMethod } from '@soundxyz/utils/const';
import { safeTransferFromAbi } from '@abi/safeTransferFrom';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { useConnectedAuth } from '@context/auth/connected';
import { Transaction } from '@context/transaction';
import { chainFromId } from '@utils/chain';
import { useTransactionFlow } from './useTransactionFlow';

export const useTransferToken = ({
  chainId,
  contractAddress,
  releaseId,
  tokenId,
  transferToAddress,
}: {
  chainId: number;
  contractAddress: Address;
  releaseId: string;
  tokenId: string;
  transferToAddress: Address;
}) => {
  const [isTransferring, setIsTransferring] = useState(false);

  const { getSigner, walletAddress, getDeepLink } = useConnectedAuth();
  const { safeHandleTransaction } = Transaction.useContainer();

  const onGiftHandle = useStableCallback(async () => {
    const signer = await getSigner(chainId);

    /**
     * This is critical for a better mobile UX.
     * We have to set before the actual transaction is sent.
     * This way it can open the user's wallet app and make it a seamless flow
     */
    const deepLink = getDeepLink();
    if (deepLink) {
      window.location.href = deepLink;
    }

    return signer.writeContract({
      address: contractAddress,
      abi: safeTransferFromAbi,
      functionName: 'safeTransferFrom',
      args: [walletAddress as Address, transferToAddress, tokenId],
      chain: chainFromId(chainId),
    });
  });

  const transferToken = useTransactionFlow({
    chainId,
    onChainSwitchFail() {
      setIsTransferring(false);
    },
    async onAuth() {
      setIsTransferring(true);

      await safeHandleTransaction({
        contractMethod: ContractMethod.COLLECTOR__TRANSFER_EDITION,
        transactionFn: onGiftHandle,
        chainId,
        openProcessingModal: true,
        showOpenWalletToast: false,
        releaseId,
      });

      setIsTransferring(false);
    },
  });

  return { transferToken, isTransferring, setIsTransferring };
};
